import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Copy,
  ExternalLink,
  FileKey,
  KeyRound,
  LockKeyhole,
  PlugZap,
  RefreshCw,
  Settings,
  ShieldCheck,
  Terminal,
  Users,
  X,
} from 'lucide-react';

interface McpBzDepenseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'vue' | 'cle' | 'config' | 'env' | 'validation' | 'securite';

const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'vue', label: 'Vue d’ensemble', icon: PlugZap },
  { id: 'cle', label: 'Générer la clé', icon: KeyRound },
  { id: 'config', label: 'config.yaml', icon: Settings },
  { id: 'env', label: '.env', icon: FileKey },
  { id: 'validation', label: 'Validation', icon: ClipboardCheck },
  { id: 'securite', label: 'Sécurité', icon: ShieldCheck },
];

function Panel({ title, icon: Icon, children, tone = 'cyan' }: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  tone?: 'cyan' | 'amber' | 'emerald' | 'rose';
}) {
  const tones = {
    cyan: 'border-cyan-300/20 bg-cyan-400/5',
    amber: 'border-amber-300/25 bg-amber-400/10',
    emerald: 'border-emerald-300/20 bg-emerald-400/5',
    rose: 'border-rose-300/25 bg-rose-400/10',
  };

  return (
    <section className={`rounded-2xl border p-5 ${tones[tone]}`}>
      <h3 className="mb-4 flex items-center gap-2 text-lg font-black text-white">
        <Icon className="h-5 w-5 text-cyan-200" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function CodeBlock({ children, label = 'Copier' }: { children: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopyFailed(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <div className="group relative my-3 overflow-hidden rounded-xl border border-slate-700 bg-slate-950">
      <pre className="overflow-x-auto p-4 pr-12 text-xs leading-6 text-emerald-300"><code>{children}</code></pre>
      <button
        type="button"
        onClick={() => void copy()}
        className="absolute right-2 top-2 rounded-lg border border-slate-600 bg-slate-800 p-2 text-slate-300 hover:bg-slate-700"
        title={label}
        aria-label={label}
      >
        {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
      </button>
      <span className="sr-only" aria-live="polite">
        {copied ? 'Contenu copié.' : copyFailed ? 'La copie a échoué.' : ''}
      </span>
    </div>
  );
}

function Steps({ items }: { items: React.ReactNode[] }) {
  return (
    <ol className="space-y-3 text-sm leading-6 text-slate-200">
      {items.map((item, index) => (
        <li key={index} className="flex gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-cyan-400/15 font-black text-cyan-200">
            {index + 1}
          </span>
          <span>{item}</span>
        </li>
      ))}
    </ol>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-2 text-sm leading-6 text-slate-300">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-400" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function VueTab() {
  return (
    <div className="space-y-5">
      <Panel title="Objectif" icon={PlugZap}>
        <p className="text-sm leading-6 text-slate-300">
          Connecter Hermes Agent au serveur MCP <strong className="text-white">BZ Dépense</strong> afin que ses outils de gestion des dépenses deviennent disponibles dans les conversations autorisées.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {[
            ['Nom du serveur', 'bz-depense'],
            ['Transport', 'MCP HTTP distant'],
            ['Point de terminaison', 'https://bzdepenses.bzinc.ca/api/mcp'],
            ['Authentification', 'Jeton Bearer conservé dans .env'],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-cyan-300">{label}</p>
              <p className="mt-1 break-words text-sm font-semibold text-white">{value}</p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Utilisateurs autorisés actuellement" icon={Users} tone="amber">
        <Bullets items={[
          <><strong className="text-white">Eric Durand</strong> peut se connecter au portail de génération.</>,
          <><strong className="text-white">Marc-Antoine Caron</strong> peut se connecter au portail de génération.</>,
          <>Toute demande d’accès pour une autre personne doit être approuvée avant de générer une clé.</>,
        ]} />
      </Panel>

      <Panel title="Principe de configuration" icon={LockKeyhole} tone="emerald">
        <p className="text-sm leading-6 text-slate-300">
          Le fichier <code className="text-emerald-300">config.yaml</code> contient seulement la référence <code className="text-emerald-300">${'{BZ_DEPENSE_MCP_KEY}'}</code>. La valeur réelle de la clé reste exclusivement dans <code className="text-emerald-300">.env</code> et ne doit jamais être déposée dans Git, PRIsME ou une conversation.
        </p>
      </Panel>
    </div>
  );
}

function CleTab() {
  return (
    <div className="space-y-5">
      <Panel title="Générer une clé MCP BZ Dépense" icon={KeyRound}>
        <Steps items={[
          <>Ouvrir le portail sécurisé <a className="font-bold text-cyan-300 underline hover:text-cyan-200" href="https://bz-mcp-next.apps.bzhosting.ca/" target="_blank" rel="noreferrer">bz-mcp-next.apps.bzhosting.ca</a>.</>,
          <>Se connecter avec le compte autorisé d’<strong className="text-white">Eric Durand</strong> ou de <strong className="text-white">Marc-Antoine Caron</strong>.</>,
          <>Créer une clé destinée à l’instance Hermes visée et lui donner un nom permettant d’identifier clairement la machine ou le profil.</>,
          <>Copier la clé une seule fois vers le fichier <code className="text-emerald-300">.env</code> de l’instance Hermes. Ne pas la coller dans <code className="text-emerald-300">config.yaml</code>.</>,
        ]} />
        <a
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-cyan-400 px-4 py-2 text-sm font-black text-slate-950 hover:bg-cyan-300"
          href="https://bz-mcp-next.apps.bzhosting.ca/"
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink className="h-4 w-4" />
          Ouvrir le générateur de clés
        </a>
      </Panel>

      <Panel title="Avant de continuer" icon={AlertTriangle} tone="rose">
        <Bullets items={[
          'Ne jamais utiliser une clé reçue dans un courriel, un document partagé ou une conversation.',
          'Ne jamais réutiliser une clé personnelle sur plusieurs installations si le portail permet de créer des clés distinctes.',
          'Si la clé a été exposée, la révoquer immédiatement dans le portail et en générer une nouvelle.',
        ]} />
      </Panel>
    </div>
  );
}

const yamlConfig = `mcp_servers:
  bz-depense:
    url: https://bzdepenses.bzinc.ca/api/mcp
    headers:
      Authorization: "Bearer \${BZ_DEPENSE_MCP_KEY}"
    enabled: true`;

function ConfigTab() {
  return (
    <div className="space-y-5">
      <Panel title="Où trouver et ouvrir config.yaml" icon={FileKey} tone="cyan">
        <p className="text-sm leading-6 text-slate-300">
          Par défaut, Hermes conserve sa configuration dans <code className="text-emerald-300">~/.hermes/config.yaml</code>. Pour l’utilisateur <code className="text-emerald-300">root</code>, le chemin complet est <code className="text-emerald-300">/root/.hermes/config.yaml</code>.
        </p>
        <p className="mt-3 text-sm leading-6 text-slate-300">Méthode recommandée pour ouvrir le bon fichier du profil Hermes actif :</p>
        <CodeBlock>{`hermes config edit`}</CodeBlock>
        <p className="mt-3 text-sm leading-6 text-slate-300">Ou, sur une installation exécutée par <code className="text-emerald-300">root</code>, l’ouvrir directement avec Nano :</p>
        <CodeBlock>{`nano /root/.hermes/config.yaml`}</CodeBlock>
        <div className="rounded-xl border border-amber-300/20 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
          Le nom exact est <code>config.yaml</code> : il n’y a pas de point devant <code>config</code>, et l’extension est <code>.yaml</code>, pas <code>.yamal</code>.
        </div>
      </Panel>

      <Panel title="Configuration attendue dans config.yaml" icon={Settings}>
        <p className="text-sm leading-6 text-slate-300">
          Le fichier actif se trouve sous <code className="text-emerald-300">{'${HERMES_HOME:-$HOME/.hermes}/config.yaml'}</code>. Le résultat attendu est le bloc suivant :
        </p>
        <CodeBlock label="Copier le YAML">{yamlConfig}</CodeBlock>
      </Panel>

      <Panel title="Méthode recommandée — commandes Hermes" icon={Terminal} tone="emerald">
        <p className="text-sm leading-6 text-slate-300">
          Utiliser ces commandes évite de corrompre le YAML par une mauvaise indentation. Les guillemets simples autour du Bearer empêchent le shell de remplacer la variable pendant la configuration.
        </p>
        <CodeBlock>{`hermes config set mcp_servers.bz-depense.url https://bzdepenses.bzinc.ca/api/mcp
hermes config set mcp_servers.bz-depense.headers.Authorization 'Bearer \${BZ_DEPENSE_MCP_KEY}'
hermes config set mcp_servers.bz-depense.enabled true`}</CodeBlock>
        <p className="text-xs leading-5 text-amber-200">
          Si une section <code>mcp_servers</code> existe déjà, ces commandes ajoutent ou mettent à jour uniquement <code>bz-depense</code>; elles ne doivent pas remplacer les autres serveurs MCP.
        </p>
      </Panel>

      <Panel title="Contrôle sans afficher la clé" icon={ClipboardCheck}>
        <CodeBlock>{`hermes config get mcp_servers.bz-depense`}</CodeBlock>
        <p className="text-sm leading-6 text-slate-300">
          La valeur d’autorisation doit demeurer <code className="text-emerald-300">Bearer ${'{BZ_DEPENSE_MCP_KEY}'}</code>. Une vraie clé visible ici indique une mauvaise configuration et doit être retirée du YAML.
        </p>
      </Panel>
    </div>
  );
}

function EnvTab() {
  return (
    <div className="space-y-5">
      <Panel title="Ajouter la clé dans .env" icon={FileKey}>
        <p className="text-sm leading-6 text-slate-300">
          Ouvrir le fichier <code className="text-emerald-300">{'${HERMES_HOME:-$HOME/.hermes}/.env'}</code> avec un éditeur local sécurisé :
        </p>
        <CodeBlock>{`nano "\${HERMES_HOME:-$HOME/.hermes}/.env"`}</CodeBlock>
        <p className="text-sm leading-6 text-slate-300">Ajouter une seule ligne en remplaçant le texte indicatif par la clé générée :</p>
        <CodeBlock label="Copier le modèle">{`BZ_DEPENSE_MCP_KEY=COLLER_LA_CLE_GENEREE_ICI`}</CodeBlock>
        <div className="rounded-xl border border-rose-300/25 bg-rose-400/10 p-4 text-sm font-semibold leading-6 text-rose-100">
          Ne jamais copier la vraie clé dans une commande partagée, une capture d’écran, un billet, Git ou PRIsME. La valeur ci-dessus est uniquement un indicateur.
        </div>
      </Panel>

      <Panel title="Protéger le fichier" icon={LockKeyhole} tone="emerald">
        <CodeBlock>{`chmod 600 "\${HERMES_HOME:-$HOME/.hermes}/.env"`}</CodeBlock>
        <p className="text-sm leading-6 text-slate-300">Vérifier les permissions sans lire le contenu du fichier :</p>
        <CodeBlock>{`stat -c '%A %U:%G %n' "\${HERMES_HOME:-$HOME/.hermes}/.env"`}</CodeBlock>
        <p className="rounded-xl bg-black/20 p-3 font-mono text-xs text-emerald-300">Résultat attendu : -rw------- UTILISATEUR:GROUPE .../.hermes/.env</p>
      </Panel>

      <Panel title="Attention aux doublons" icon={AlertTriangle} tone="amber">
        <p className="text-sm leading-6 text-slate-300">
          Il doit y avoir une seule définition active de <code className="text-emerald-300">BZ_DEPENSE_MCP_KEY</code>. Si elle existe déjà, remplacer sa valeur au lieu d’ajouter une deuxième ligne.
        </p>
      </Panel>
    </div>
  );
}

function ValidationTab() {
  return (
    <div className="space-y-5">
      <Panel title="Recharger Hermes" icon={RefreshCw}>
        <p className="text-sm leading-6 text-slate-300">
          Redémarrer le processus Hermes ou sa passerelle après la modification. Les serveurs MCP et les variables de <code>.env</code> sont chargés au démarrage.
        </p>
      </Panel>

      <Panel title="Tester la connexion MCP" icon={ClipboardCheck} tone="emerald">
        <CodeBlock>{`hermes mcp list
hermes mcp test bz-depense`}</CodeBlock>
        <Bullets items={[
          <><code className="text-emerald-300">bz-depense</code> apparaît comme serveur configuré et activé.</>,
          <>Le test se connecte à <code className="text-emerald-300">https://bzdepenses.bzinc.ca/api/mcp</code>.</>,
          <>La découverte retourne les outils BZ Dépense sans erreur d’authentification.</>,
          <>Aucune valeur de clé n’est affichée dans la sortie ou les journaux partagés.</>,
        ]} />
      </Panel>

      <Panel title="Critère de réussite" icon={CheckCircle2}>
        <p className="text-sm leading-6 text-slate-300">
          La configuration est réussie seulement lorsque <code className="text-emerald-300">hermes mcp test bz-depense</code> confirme la connexion et découvre les outils. La simple présence du bloc YAML ne suffit pas.
        </p>
      </Panel>

      <Panel title="Dépannage rapide" icon={AlertTriangle} tone="amber">
        <Bullets items={[
          <>Erreur <strong className="text-white">401/403</strong> : vérifier que la clé est valide, non révoquée et associée au bon accès; ne jamais l’afficher pour la comparer.</>,
          <>Variable absente : confirmer que le fichier modifié est celui du profil Hermes actif, sous <code className="text-emerald-300">$HERMES_HOME</code>.</>,
          <>Serveur absent : vérifier le nom exact <code className="text-emerald-300">bz-depense</code> et redémarrer Hermes.</>,
          <>Connexion impossible : valider la résolution DNS et l’accès HTTPS à <code className="text-emerald-300">bzdepenses.bzinc.ca</code>.</>,
        ]} />
      </Panel>
    </div>
  );
}

function SecuriteTab() {
  return (
    <div className="space-y-5">
      <Panel title="Règles non négociables" icon={ShieldCheck} tone="rose">
        <Bullets items={[
          'La clé MCP est un secret : elle ne doit jamais être copiée dans config.yaml, Git, PRIsME, une procédure, un billet ou une conversation.',
          'Le fichier .env doit demeurer hors du dépôt et protégé avec des permissions 0600.',
          'Une clé doit être révoquée dès qu’elle est soupçonnée d’avoir été exposée.',
          'Les droits accordés à la clé doivent respecter le moindre privilège et le rôle réel de l’instance Hermes.',
          'Les actions financières ou irréversibles doivent conserver une validation humaine conformément à la gouvernance BZ Dépense.',
        ]} />
      </Panel>

      <Panel title="Responsabilités" icon={Users}>
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/10 text-cyan-200">
              <tr><th className="px-4 py-3 text-left">Rôle</th><th className="px-4 py-3 text-left">Responsabilité</th></tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-t border-white/10"><td className="px-4 py-3 font-bold text-white">Eric Durand / Marc-Antoine Caron</td><td className="px-4 py-3">Accéder au portail autorisé et générer ou révoquer les clés selon les besoins approuvés.</td></tr>
              <tr className="border-t border-white/10"><td className="px-4 py-3 font-bold text-white">Administrateur Hermes</td><td className="px-4 py-3">Installer la référence dans config.yaml, conserver la clé dans .env, protéger les permissions et valider la connexion.</td></tr>
              <tr className="border-t border-white/10"><td className="px-4 py-3 font-bold text-white">Utilisateur de l’agent</td><td className="px-4 py-3">Respecter les approbations, la confidentialité et les limites des outils BZ Dépense.</td></tr>
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

export default function McpBzDepenseModal({ isOpen, onClose }: McpBzDepenseModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('vue');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    setActiveTab('vue');
    const focusFrame = window.requestAnimationFrame(() => dialogRef.current?.focus());

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ));
        if (focusable.length === 0) {
          event.preventDefault();
          dialogRef.current.focus();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (document.activeElement === dialogRef.current || !dialogRef.current.contains(document.activeElement)) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      previouslyFocusedElement?.focus();
    };
  }, [isOpen, onClose]);

  const content = {
    vue: <VueTab />,
    cle: <CleTab />,
    config: <ConfigTab />,
    env: <EnvTab />,
    validation: <ValidationTab />,
    securite: <SecuriteTab />,
  }[activeTab];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-3 backdrop-blur-sm md:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={onClose}
          role="dialog"
          aria-modal="true"
          aria-labelledby="mcp-bz-depense-modal-title"
        >
          <motion.div
            ref={dialogRef}
            tabIndex={-1}
            className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-cyan-300/20 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 shadow-2xl outline-none"
            initial={{ y: 30, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 20, scale: 0.98 }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-4 border-b border-white/10 bg-black/20 px-5 py-5 md:px-7">
              <div className="flex gap-4">
                <div className="rounded-2xl bg-cyan-400/10 p-3"><PlugZap className="h-7 w-7 text-cyan-300" /></div>
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-300">Procédure interne BZ</p>
                  <h2 id="mcp-bz-depense-modal-title" className="mt-1 text-2xl font-black text-white md:text-3xl">MCP BZ Dépense</h2>
                  <p className="mt-1 text-sm text-slate-300">Hermes Agent · configuration sécurisée · validation de connexion</p>
                </div>
              </div>
              <button type="button" onClick={onClose} className="rounded-xl border border-white/10 bg-white/5 p-2 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </header>

            <nav role="tablist" className="flex gap-2 overflow-x-auto border-b border-white/10 px-4 py-3 md:px-7" aria-label="Sections de la procédure MCP BZ Dépense">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  id={`mcp-bz-depense-tab-${id}`}
                  role="tab"
                  aria-selected={activeTab === id}
                  aria-controls={`mcp-bz-depense-panel-${id}`}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold transition ${activeTab === id ? 'bg-cyan-400 text-slate-950' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}
                >
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </nav>

            <div id={`mcp-bz-depense-panel-${activeTab}`} role="tabpanel" aria-labelledby={`mcp-bz-depense-tab-${activeTab}`} className="overflow-y-auto p-4 md:p-7">
              {content}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
